/**
 * The CRM's half of the macOS app bridge.
 *
 * The desktop app (see `desktop/` in this repo) is a Tauri shell that loads
 * this very site and injects one object, `window.__WA_DESKTOP__`. Everything
 * the CRM knows about running inside an app goes through this module, for two
 * reasons. The CRM must stay a plain web app that happens to notice it is in a
 * shell: nothing here imports a Tauri package, and every call is a no-op in a
 * browser. And the number on the Dock has to come from the same place as the
 * number on the bell, because a Dock badge that disagrees with the bell it
 * opens teaches people to stop reading both.
 */
import type { SignalRow } from "../crm/shell/signals";

/** The shape the app injects. Declared here because nothing types it for us. */
interface DesktopBridge {
  version: number;
  platform: string;
  setBadge: (count: number) => Promise<unknown>;
  notify: (title: string, body: string) => Promise<unknown>;
  currentServer: () => Promise<string>;
}

declare global {
  interface Window {
    __WA_DESKTOP__?: DesktopBridge;
  }
}

function bridge(): DesktopBridge | null {
  if (typeof window === "undefined") return null;
  return window.__WA_DESKTOP__ ?? null;
}

/** True when this page is running inside the macOS app. */
export function isDesktopApp(): boolean {
  return bridge() !== null;
}

/** Puts a number on the Dock icon. Silent no-op in a browser. */
export function setBadge(count: number): void {
  void bridge()?.setBadge(count);
}

/** Posts one macOS notification. Silent no-op in a browser. */
export function notify(title: string, body: string): void {
  void bridge()?.notify(title, body);
}

/** Counts by signal key, as last seen by the desktop bridge. */
export type SignalCounts = Record<string, number>;

/** Turns the current rows into the map this module compares against. */
export function countsOf(rows: SignalRow[]): SignalCounts {
  const out: SignalCounts = {};
  for (const row of rows) out[row.key] = row.count;
  return out;
}

/**
 * Which rows grew since the last look, and therefore deserve a notification.
 *
 * A row is worth announcing when its count went UP. Not when it merely exists:
 * every row exists at the moment the app starts, and announcing all of them
 * would greet the operator with a stack of banners for work that has been
 * sitting there since yesterday. Not when it shrinks either, because that is
 * somebody clearing the row, which is the opposite of news.
 *
 * `previous` being null means "we have not looked yet", and the honest answer
 * to that is nothing at all: the first look only establishes the baseline.
 */
export function raisedSince(rows: SignalRow[], previous: SignalCounts | null): SignalRow[] {
  if (previous === null) return [];
  return rows.filter((row) => row.count > (previous[row.key] ?? 0));
}
